import React, { useState, useEffect, useMemo } from 'react';
import { getPlans, createSubscription, getGatewayConfig } from '../core/subscriptionService';
import { Plan } from '../core/_models';
import { useToast } from '../../components/Toats';
import Select from 'react-select';
import { getClientAll } from '../../../services/clientService';
import { getPlantsAllSimple } from '../../../services/plantsService';
import { getUnitBranchAllSimple } from '../../../services/unitBranchService';
import { getReactSelectStyles } from '../core/reactSelectTheme';
import { useThemeMode } from '../../../../_metronic/partials/layout/theme-mode/ThemeModeProvider';

export const LinkGenerator: React.FC = () => {
  const [plans, setPlans] = useState<Plan[]>([]);
  const [selectedPlanId, setSelectedPlanId] = useState<string>('');
  const [kwp, setKwp] = useState<string>('5.5');
  const [panels, setPanels] = useState<string>('12');
  const [plantPower, setPlantPower] = useState<number>(0);
  const [clients, setClients] = useState<any[]>([]);
  const [plants, setPlants] = useState<any[]>([]);
  const [branches, setBranches] = useState<any[]>([]);

  const [selectedClientId, setSelectedClientId] = useState<string>('');
  const [selectedPlantId, setSelectedPlantId] = useState<string>('');
  const [selectedBranchId, setSelectedBranchId] = useState<string>('');

  const [expiresInDays, setExpiresInDays] = useState<number>(3);
  const [billingFrequency, setBillingFrequency] = useState<string>('MENSAL');
  const [autoRenew, setAutoRenew] = useState<boolean>(true);
  const [generating, setGenerating] = useState(false);
  const [gatewayActive, setGatewayActive] = useState<boolean>(true);
  const [generatedLink, setGeneratedLink] = useState<string>('');
  const { toggleToast } = useToast();
  const { mode } = useThemeMode();

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const selectStyles = useMemo(() => getReactSelectStyles(), [mode]);

  useEffect(() => {
    getPlans().then(data => {
      setPlans(data);
      if (data.length > 0) {
        setSelectedPlanId(data[0].id);
      }
    });
    getUnitBranchAllSimple().then(data => {
      setBranches(data?.data || data || []);
    });
    getGatewayConfig().then(data => {
      setGatewayActive(!!data?.is_active);
    }).catch(() => {
      setGatewayActive(false);
    });
  }, []);

  useEffect(() => {
    if (selectedBranchId) {
      getClientAll(selectedBranchId).then(data => {
        setClients(data?.data || data || []);
      }).catch(() => {
        setClients([]);
      });
    } else {
      setClients([]);
    }
    setSelectedClientId('');
    setPlants([]);
    setSelectedPlantId('');
    setKwp('');
    setPanels('');
    setPlantPower(0);
  }, [selectedBranchId]);

  useEffect(() => {
    if (selectedClientId) {
      getPlantsAllSimple(`?client=${selectedClientId}`).then(data => {
        setPlants(data?.data || data || []);
      }).catch(() => {
        setPlants([]);
      });
    } else {
      setPlants([]);
    }
    setSelectedPlantId('');
    setKwp('');
    setPanels('');
    setPlantPower(0);
  }, [selectedClientId]);

  const handleGenerateLink = async () => {
    if (!selectedPlanId) {
      toggleToast('Selecione um plano.', 'warning');
      return;
    }

    try {
      setGenerating(true);
      const payload = {
        plan_id: selectedPlanId,
        client_id: selectedClientId,
        plant_id: selectedPlantId,
        unitBranch_id: selectedBranchId,
        expires_in_days: expiresInDays,
        billing_frequency: billingFrequency,
        auto_renew: autoRenew,
        kwp: kwp ? Number(kwp) : undefined,
        panels: panels ? Number(panels) : undefined,
      };

      const response = await createSubscription(payload);
      if (response && response.access_link_token) {
        const link = `${window.location.origin}/checkout/${response.access_link_token}`;
        setGeneratedLink(link);
        toggleToast('Link gerado com sucesso!', 'success');
      }
    } catch (error: any) {
      console.error(error);
      toggleToast(error?.response?.data?.message || error?.response?.data?.error || 'Erro ao gerar link. Verifique os IDs obrigatórios.', 'error');
    } finally {
      setGenerating(false);
    }
  };

  const copyToClipboard = () => {
    if (generatedLink) {
      navigator.clipboard.writeText(generatedLink);
      toggleToast('Link copiado!', 'success');
    }
  };

  const selectedPlan = plans.find(p => p.id === selectedPlanId);

  const estimatedMonthly = useMemo(() => {
    if (!selectedPlan?.planItems || selectedPlan.planItems.length === 0) return Number(selectedPlan?.base_value || 0);

    const itemsTotal = selectedPlan.planItems.reduce((sum: number, item: any) => {
      let qty: number;

      switch (item.quantity_mode) {
        case 'FIXED':
          qty = Number(item.default_quantity) || 1;
          break;
        case 'PER_PANEL':
          qty = Number(panels) || 0;
          break;
        case 'PER_KWP':
          qty = plantPower ? plantPower / 1000 : 0;
          break;
        case 'PER_POWER':
          qty = plantPower || 0;
          break;
        default:
          qty = 1;
      }

      return sum + (Number(item.unit_price) || 0) * qty;
    }, 0);

    return Number(selectedPlan?.base_value || 0) + itemsTotal;
  }, [selectedPlan, panels, plantPower]);

  return (
    <div className='row g-5 g-xl-8'>
      <div className='col-xl-7'>
        <div className='card mb-5 mb-xl-8'>
          {!gatewayActive && (
            <div className='notice d-flex bg-light-danger rounded border-danger border border-dashed p-6 mx-8 mt-8'>
              <i className='bi bi-exclamation-octagon fs-2tx text-danger me-4'></i>
              <div className='d-flex flex-stack flex-grow-1'>
                <div className='fw-bold'>
                  <h4 className='text-gray-900 fw-bolder'>Gateway de Pagamento Inativo</h4>
                  <div className='fs-6 text-gray-700'>
                    Não é possível gerar links de assinatura pois o gateway de pagamento não está configurado ou ativo. Acesse as configurações de Gateway.
                  </div>
                </div>
              </div>
            </div>
          )}
          <div className='card-body p-8'>
            <div className='d-flex align-items-center mb-6'>
              <div className='symbol symbol-40px me-4'>
                <span className='symbol-label bg-light-primary'>
                  <i className='bi bi-calendar2-week text-primary fs-3'></i>
                </span>
              </div>
              <div className='d-flex align-items-center gap-3'>
                <h3 className='card-title fw-bolder text-gray-800 mb-0 fs-2'>1. Seleção de Plano</h3>
              </div>
            </div>
            <div className='row g-4 mb-8'>
              {plans.map((plan) => (
                <div className='col-4 position-relative' key={plan.id}>
                  {plan.periodicity === 'SEMESTRAL' && (
                    <span className='badge badge-primary position-absolute top-0 start-50 translate-middle mt-2 z-index-1'>POPULAR</span>
                  )}
                  <label className={`btn btn-outline btn-outline-dashed btn-active-light-primary d-flex flex-column text-start p-6 ${selectedPlanId === plan.id ? 'active border-primary' : ''}`}>
                    <input className='btn-check' type='radio' name='planType' value={plan.id} checked={selectedPlanId === plan.id} onChange={(e) => setSelectedPlanId(e.target.value)} />
                    <span className='d-flex mb-2'>
                      <span className='fw-bolder fs-6'>{plan.periodicity}</span>
                    </span>
                    <span className='fs-3 fw-bolder text-gray-800 mb-1'>{plan.name}</span>
                    <span className='fs-7 text-muted'>
                      {plan.default_discount && plan.default_discount > 0 ? (
                        <span className='text-primary fw-bolder'>{plan.default_discount}% OFF</span>
                      ) : (
                        'Sem desconto'
                      )}
                    </span>
                  </label>
                </div>
              ))}
              {plans.length === 0 && (
                <div className='col-12 text-center text-muted'>
                  Nenhum plano disponível.
                </div>
              )}
            </div>

            <div className='separator separator-dashed my-8'></div>

            <div className='d-flex align-items-center mb-6'>
              <div className='symbol symbol-40px me-4'>
                <span className='symbol-label bg-light-primary'>
                  <i className='bi bi-gear-wide-connected text-primary fs-3'></i>
                </span>
              </div>
              <h3 className='card-title fw-bolder text-gray-800 mb-0 fs-2'>2. Dados Técnicos da Unidade</h3>
            </div>

            <div className='row g-5 mb-8'>
              <div className='col-md-12'>
                <label className='form-label fw-bolder text-gray-800 fs-6'>FILIAL</label>
                <Select
                  options={branches.map(b => ({ value: b.id, label: b.companyName || b.name || 'Filial Sem Nome' }))}
                  onChange={(val: any) => setSelectedBranchId(val?.value || '')}
                  placeholder="Selecione a filial"
                  className="react-select-container"
                  classNamePrefix="react-select"
                  styles={selectStyles}
                />
              </div>
              <div className='col-md-6'>
                <label className='form-label fw-bolder text-gray-800 fs-6'>CLIENTE</label>
                <Select
                  options={clients.map(c => ({ value: c.id, label: c.nameResponsible || c.name || 'Cliente Sem Nome' }))}
                  value={selectedClientId ? { value: selectedClientId, label: clients.find(c => c.id === selectedClientId)?.nameResponsible || clients.find(c => c.id === selectedClientId)?.name || 'Cliente' } : null}
                  onChange={(val: any) => setSelectedClientId(val?.value || '')}
                  placeholder={selectedBranchId ? "Selecione um cliente" : "Selecione uma filial primeiro"}
                  className="react-select-container"
                  classNamePrefix="react-select"
                  styles={selectStyles}
                  isDisabled={!selectedBranchId}
                  isClearable
                />
              </div>
              <div className='col-md-6'>
                <label className='form-label fw-bolder text-gray-800 fs-6'>USINA</label>
                <Select
                  options={plants.map(p => ({ value: p.id, label: p.name || p.identifier }))}
                  value={selectedPlantId ? { value: selectedPlantId, label: plants.find(p => p.id === selectedPlantId)?.name || plants.find(p => p.id === selectedPlantId)?.identifier || 'Usina' } : null}
                  onChange={(val: any) => {
                    setSelectedPlantId(val?.value || '');
                    const plant = plants.find(p => p.id === val?.value);
                    if (plant) {
                      setKwp(plant.capacity ? String(plant.capacity) : (plant.power ? String(plant.power) : ''));
                      setPanels(plant.number_panels ? String(plant.number_panels) : '');
                      setPlantPower(Number(plant.power) || 0);
                    } else {
                      setKwp('');
                      setPanels('');
                      setPlantPower(0);
                    }
                  }}
                  placeholder={selectedClientId ? "Selecione uma usina" : "Selecione um cliente primeiro"}
                  className="react-select-container"
                  classNamePrefix="react-select"
                  styles={selectStyles}
                  isDisabled={!selectedClientId}
                  isClearable
                />
              </div>
              <div className='col-md-6'>
                <label className='form-label fw-bolder text-gray-800 fs-6'>POTÊNCIA DA USINA (kWp)</label>
                <div className='input-group input-group-solid'>
                  <span className='input-group-text'>
                    <i className='bi bi-lightning-charge fs-4'></i>
                  </span>
                  <input
                    type='number'
                    className='form-control form-control-solid'
                    placeholder='Ex: 5.5'
                    value={kwp}
                    onChange={(e) => setKwp(e.target.value)}
                    onBlur={(e) => setPlantPower(Number(e.target.value) * 1000)}
                  />
                  <span className='input-group-text'>kWp</span>
                </div>
              </div>
              <div className='col-md-6'>
                <label className='form-label fw-bolder text-gray-800 fs-6'>NÚMERO DE PAINÉIS</label>
                <div className='input-group input-group-solid'>
                  <span className='input-group-text'>
                    <i className='bi bi-grid-3x3 fs-4'></i>
                  </span>
                  <input
                    type='number'
                    className='form-control form-control-solid'
                    placeholder='Ex: 12'
                    value={panels}
                    onChange={(e) => setPanels(e.target.value)}
                  />
                  <span className='input-group-text'>un.</span>
                </div>
              </div>
              {selectedPlantId && (!kwp || !panels) && (
                <div className='col-12'>
                  <div className='notice d-flex bg-light-warning rounded border-warning border border-dashed p-4 mt-2'>
                    <i className='bi bi-exclamation-triangle fs-2tx text-warning me-4'></i>
                    <div className='d-flex flex-stack flex-grow-1'>
                      <div className='fw-bold'>
                        <h4 className='text-gray-900 fw-bolder'>Atenção</h4>
                        <div className='fs-6 text-gray-700'>
                          A usina selecionada não possui Potência Instalada ou Número de Painéis cadastrados. Isso pode resultar em um valor zerado no checkout dependendo do plano escolhido. Atualize o cadastro da usina.
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
              <div className='col-md-6'>
                <label className='form-label fw-bolder text-gray-800 fs-6'>VALIDADE DO LINK (DIAS)</label>
                <select className='form-select form-select-solid' value={expiresInDays} onChange={(e) => setExpiresInDays(Number(e.target.value))}>
                  <option value={1}>1 dia</option>
                  <option value={3}>3 dias</option>
                  <option value={7}>7 dias</option>
                  <option value={15}>15 dias</option>
                </select>
              </div>

              {selectedPlan && selectedPlan.periodicity !== 'MENSAL' && (
                <div className='col-md-6'>
                  <label className='form-label fw-bolder text-gray-800 fs-6'>FREQUÊNCIA DE COBRANÇA</label>
                  <select
                    className='form-select form-select-solid'
                    value={billingFrequency}
                    onChange={(e) => setBillingFrequency(e.target.value)}
                  >
                    <option value='MENSAL'>
                      Mensal ({selectedPlan.periodicity === 'ANUAL' ? '12x' : selectedPlan.periodicity === 'SEMESTRAL' ? '6x' : '3x'})
                    </option>
                    {(selectedPlan.periodicity === 'ANUAL' || selectedPlan.periodicity === 'SEMESTRAL') && (
                      <option value='TRIMESTRAL'>
                        Trimestral ({selectedPlan.periodicity === 'ANUAL' ? '4x' : '2x'})
                      </option>
                    )}
                    {selectedPlan.periodicity === 'ANUAL' && (
                      <option value='SEMESTRAL'>Semestral (2x)</option>
                    )}
                    <option value='A_VISTA'>À Vista</option>
                  </select>
                </div>
              )}

              <div className='col-md-6'>
                <label className='form-label fw-bolder text-gray-800 fs-6'>RENOVAÇÃO AUTOMÁTICA</label>
                <div className='form-check form-check-solid form-switch mt-3'>
                  <input
                    className='form-check-input w-45px h-30px'
                    type='checkbox'
                    checked={autoRenew}
                    onChange={(e) => setAutoRenew(e.target.checked)}
                  />
                  <label className='form-check-label fw-bold text-gray-600 ms-3'>
                    {autoRenew ? 'Renovar ao final do ciclo' : 'Não renovar'}
                  </label>
                </div>
              </div>
            </div>

            <div className='d-flex gap-4 mt-10'>
              <button
                className='btn btn-primary flex-grow-1 fs-5 fw-bolder'
                onClick={handleGenerateLink}
                disabled={generating || !gatewayActive}
                title={!gatewayActive ? 'Gateway inativo' : ''}
              >
                {generating ? (
                  <span className='indicator-progress d-block'>
                    Gerando... <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
                  </span>
                ) : (
                  <>
                    <i className='bi bi-link-45deg fs-2 me-2'></i>
                    <span>GERAR LINK</span>
                  </>
                )}
              </button>
            </div>

            {generatedLink && (
              <div className='mt-6 p-4 bg-light-success rounded border border-success border-dashed'>
                <span className='text-success fw-bolder d-block mb-2'>Link gerado com sucesso:</span>
                <div className='d-flex align-items-center bg-body rounded p-3 border border-gray-300'>
                  <span className='text-gray-800 fs-7 text-truncate me-3' style={{ userSelect: 'all' }}>{generatedLink}</span>
                  <button className='btn btn-icon btn-sm btn-active-light-primary ms-auto' onClick={copyToClipboard} title="Copiar link">
                    <i className='bi bi-clipboard fs-4'></i>
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className='col-xl-5'>
        <div className='d-flex flex-column h-100'>
          <div className='d-flex align-items-center mb-4'>
            <i className='bi bi-eye fs-2 text-gray-500 me-2'></i>
            <span className='fw-bolder text-gray-500 text-uppercase tracking-widest fs-7'>Preview do Cliente</span>
          </div>

          <div className='flex-grow-1 d-flex justify-content-center align-items-center bg-body rounded p-8'>
            {/* CSS Smartphone Mockup */}
            <div style={{
              width: '320px',
              height: '650px',
              backgroundColor: 'var(--bs-body-bg)',
              borderRadius: '40px',
              border: '12px solid #000000',
              outline: '1px solid rgba(150, 150, 150, 0.2)',
              position: 'relative',
              boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.5)',
              overflow: 'hidden',
              display: 'flex',
              flexDirection: 'column'
            }}>
              {/* Notch */}
              <div style={{
                position: 'absolute',
                top: '0',
                left: '50%',
                transform: 'translateX(-50%)',
                width: '120px',
                height: '25px',
                backgroundColor: '#000000',
                borderBottomLeftRadius: '20px',
                borderBottomRightRadius: '20px',
                zIndex: 10
              }}></div>

              {/* Phone Header Background */}
              <div style={{
                backgroundColor: 'var(--bs-primary)',
                height: '240px',
                padding: '40px 20px 20px',
                display: 'flex',
                flexDirection: 'column',
                borderBottomLeftRadius: '30px',
                borderBottomRightRadius: '30px'
              }}>
                <div className='d-flex align-items-center mb-6'>
                  <div className='symbol symbol-30px bg-white rounded-circle d-flex align-items-center justify-content-center me-3'>
                    <i className='bi bi-lightning-charge-fill text-primary fs-5'></i>
                  </div>
                  <div className='d-flex flex-column'>
                    <span className='text-white fw-bolder fs-8 text-uppercase' style={{ letterSpacing: '1px' }}>Contratação</span>
                    <span className='text-white fw-bolder fs-5'>Solar Vision</span>
                  </div>
                </div>
                <h2 className='text-white fw-bolder fs-1 mb-0' style={{ lineHeight: '1.2' }}>Olá! Vamos ativar sua usina?</h2>
              </div>

              {/* Phone Content */}
              <div className='flex-grow-1 p-5' style={{ marginTop: '-40px', zIndex: 5 }}>
                <div className='bg-body rounded p-5 shadow-sm mb-4 border border-gray-200'>
                  <span className='text-muted fw-bolder fs-8 text-uppercase d-block mb-1'>Plano Selecionado</span>
                  <div className='d-flex justify-content-between align-items-center mb-4'>
                    <div className='d-flex flex-column'>
                      <span className='text-dark fw-bolder fs-5'>
                        Plano {selectedPlan ? (selectedPlan.periodicity === 'MENSAL' ? 'Mensal' : selectedPlan.periodicity === 'SEMESTRAL' ? 'Semestral' : selectedPlan.periodicity === 'ANUAL' ? 'Anual' : selectedPlan.periodicity) : 'Não selecionado'}
                      </span>
                      <span className='text-muted fw-bold'>{selectedPlan?.name || 'N/A'}</span>
                    </div>
                    <div className='d-flex flex-column align-items-end'>
                      {selectedPlan && billingFrequency === 'MENSAL' && selectedPlan.periodicity !== 'MENSAL' ? (
                        <>
                          <span className='text-gray-800 fw-bolder fs-4'>
                            {selectedPlan.periodicity === 'ANUAL' ? '12x' : selectedPlan.periodicity === 'SEMESTRAL' ? '6x' : '3x'} de R$ {estimatedMonthly.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                            <span className='fs-7 fw-normal text-muted'> /mês</span>
                          </span>
                        </>
                      ) : (
                        <span className='text-gray-800 fw-bolder fs-3'>
                          R$ {estimatedMonthly.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                          <span className='fs-7 fw-normal text-muted'> /mês</span>
                        </span>
                      )}
                      {Number(selectedPlan?.adhesion_fee) > 0 && (
                        <span className='text-success fw-bold fs-8 mt-1 text-end'>
                          + R$ {Number(selectedPlan?.adhesion_fee).toLocaleString('pt-BR', { minimumFractionDigits: 2 })} de taxa de adesão (1ª fatura)
                        </span>
                      )}
                    </div>
                  </div>

                  <div className='row g-3'>
                    <div className='col-6'>
                      <div className='bg-light rounded p-3 text-center d-flex flex-column align-items-center'>
                        <i className='bi bi-gear-wide-connected text-primary fs-3 mb-1'></i>
                        <span className='text-muted fs-8 fw-bolder text-uppercase mb-1'>Potência</span>
                        <span className='text-dark fw-bolder fs-6'>{kwp || '0'} kWp</span>
                      </div>
                    </div>
                    <div className='col-6'>
                      <div className='bg-light rounded p-3 text-center d-flex flex-column align-items-center'>
                        <i className='bi bi-grid-3x3 text-primary fs-3 mb-1'></i>
                        <span className='text-muted fs-8 fw-bolder text-uppercase mb-1'>Painéis</span>
                        <span className='text-dark fw-bolder fs-6'>{panels || '0'} un.</span>
                      </div>
                    </div>
                  </div>
                </div>

                <div className='d-flex align-items-center px-2 mb-6'>
                  <i className='bi bi-check-circle-fill text-primary me-2'></i>
                  <span className='text-muted fw-bold fs-7'>Monitoramento 24/7 incluso</span>
                </div>

                <button className='btn btn-dark w-100 fw-bolder py-4'>
                  Confirmar Assinatura
                </button>
                <div className='text-center mt-4'>
                  <span className='text-muted fs-8'>Ambiente seguro Solar Vision</span>
                </div>
              </div>
            </div>
          </div>

          <div className='d-flex align-items-center bg-light-info rounded p-5 mt-5'>
            <i className='bi bi-info-circle text-info fs-2x me-4'></i>
            <span className='text-gray-700 fw-bold fs-7'>
              O link gerado é único para este cliente e expira conforme a validade selecionada. Você poderá acompanhar o status da assinatura na aba <b>Gestão Comercial</b>.
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};
